'use client';

import { Tag } from '@components/templates/my-jobs/Card/partials/Tag';
import { ApplicantStatus } from '@type/company-job';
import { ApiCompanyJobDetail } from '@type/v1-api-types';
import { useTranslation } from 'next-i18next';
import { getI18nLinkHref } from '@services/i18nPaths';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';

// Every chip opens the v2 pipeline, which replaced the input-flow board and the
// `/applicants?status=` rank list at 100% in prod on 2026-09-16. "New" and "All"
// carry the same query strings as the equivalent chips on the my-jobs card
// (`PIPELINE_TAGS` in `my-jobs/Card/partials/Tags.tsx`), so both surfaces land on
// the same list. All three keep `sources=applied,saved` so the row describes one
// population — people who applied or were saved — differing only by stage.
//
// There is deliberately no "Interview" chip: the pipeline has no interview-only
// scope, and the closest one (`finalist`) is STATUS_INTERVIEW *plus*
// STATUS_OFFERED, so the chip count and the list it opened would not have agreed.
const COMPANY_JOB_APPLICANTS_TAGS: {
  label: string;
  href: (jobSlug: string) => string;
  countKey?: string;
  className: string;
}[] = [
  {
    label: 'my-jobs.card.tags.new',
    countKey: `status_${ApplicantStatus.NEW}`,
    href: (jobSlug) => `/job/${jobSlug}/pipeline?sources=applied,saved&onlyNew=true`,
    className: 'text-primary hover:bg-primary/10',
  },
  {
    label: 'my-jobs.card.tags.shortlisted',
    countKey: `status_${ApplicantStatus.SHORTLIST}`,
    // `scope=shortlist` is exactly STATUS_SHORTLIST server-side
    // (ProfileSearchRepository::SCOPE_STATUSES).
    href: (jobSlug) => `/job/${jobSlug}/pipeline?sources=applied,saved&scope=shortlist`,
    className: 'text-secondary hover:bg-secondary/10',
  },
  {
    label: 'my-jobs.card.tags.all',
    countKey: 'total',
    // `scope=all` overrides the pipeline's default `scope=active` so the landing
    // list matches the `total` count on the chip — without it the default scope
    // (STATUS_NEW + STATUS_VIEWED) silently drops shortlisted/rejected/hired.
    href: (jobSlug) => `/job/${jobSlug}/pipeline?sources=applied,saved&scope=all`,
    className: 'hover:bg-[#F0F2F4] dark:hover:text-dark',
  },
];

interface ApplicantsTagsProps {
  job: ApiCompanyJobDetail;
}

export const ApplicantsTags = (props: ApplicantsTagsProps) => {
  // Destructure props
  const { job } = props;

  // Translation
  const { t } = useTranslation('common');

  // Router for locale
  const { locale } = useExtendedRouter();

  if (!job.applicationStats) return null;

  return (
    <div className="pt-8">
      <div className="mb-5 flex items-center justify-between">
        <h3 className="text-lg font-bold">
          {t('job.detail.company.applicants.title')}
        </h3>
      </div>
      <div className="flex flex-wrap gap-2">
        {COMPANY_JOB_APPLICANTS_TAGS.map((tag) => (
          <Tag
            key={tag.label}
            {...tag}
            label={t(tag.label)}
            href={getI18nLinkHref(tag.href(job.slug), locale)}
            count={
              tag.countKey ? job.applicationStats?.[tag.countKey] : undefined
            }
          />
        ))}
      </div>
    </div>
  );
};
